import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ScrollReveal from 'scrollreveal';
import styles from './Intersection.module.css';

// Import gallery images (1-26)
import grid1 from '../images/שירלי גריד רקע 1.JPG';
import grid2 from '../images/שירלי גריד רקע 2.JPG';
import grid3 from '../images/שירלי גריד רקע 3.JPG';
import grid4 from '../images/שירלי גריד רקע 4.JPG';
import grid5 from '../images/שירלי גריד רקע 5.JPG';
import grid6 from '../images/שירלי גריד רקע 6.JPG';
import grid7 from '../images/שירלי גריד רקע 7.jpg';
import grid8 from '../images/שירלי גריד רקע 8.jpg';
import grid9 from '../images/שירלי גריד רקע 9.jpg';
import grid10 from '../images/שירלי גריד רקע 10.jpg';
import grid11 from '../images/שירלי גריד רקע 11.jpg';
import grid12 from '../images/שירלי גריד רקע 12.jpg';
import grid13 from '../images/שירלי גריד רקע 13.jpg';
import grid14 from '../images/שירלי גריד רקע 14.jpg';
import grid15 from '../images/שירלי גריד רקע 15.jpg';
import grid16 from '../images/שירלי גריד רקע 16.jpg';
import grid17 from '../images/שירלי גריד רקע 17.jpg';
import grid18 from '../images/שירלי גריד רקע 18.jpg';
import grid19 from '../images/שירלי גריד רקע 19.jpg';
import grid20 from '../images/שירלי גריד רקע 20.jpg';
import grid21 from '../images/שירלי גריד רקע 21.JPG';
import grid22 from '../images/שירלי גריד רקע 22.JPG';
import grid23 from '../images/שירלי גריד רקע 23.jpg';
import grid24 from '../images/שירלי גריד רקע 24.jpg';
import grid25 from '../images/שירלי גריד רקע 25.jpg';
import grid26 from '../images/שירלי גריד רקע 26.jpg';

// Register GSAP plugins after all imports
gsap.registerPlugin(ScrollTrigger);

// Gallery images array
const galleryImages = [
  { id: 1, src: grid1, height: 'tall' },
  { id: 2, src: grid2, height: 'medium' },
  { id: 3, src: grid3, height: 'short' },
  { id: 4, src: grid4, height: 'tall' },
  { id: 5, src: grid5, height: 'medium' },
  { id: 6, src: grid6, height: 'short' },
  { id: 7, src: grid7, height: 'tall' },
  { id: 8, src: grid8, height: 'medium' },
  { id: 9, src: grid9, height: 'short' },
  { id: 10, src: grid10, height: 'tall' },
  { id: 11, src: grid11, height: 'medium' },
  { id: 12, src: grid12, height: 'short' },
  { id: 13, src: grid13, height: 'tall' },
  { id: 14, src: grid14, height: 'medium' },
  { id: 15, src: grid15, height: 'short' },
  { id: 16, src: grid16, height: 'tall' },
  { id: 17, src: grid17, height: 'medium' },
  { id: 18, src: grid18, height: 'short' },
  { id: 19, src: grid19, height: 'tall' },
  { id: 20, src: grid20, height: 'medium' },
  { id: 21, src: grid21, height: 'short' },
  { id: 22, src: grid22, height: 'tall' },
  { id: 23, src: grid23, height: 'medium' },
  { id: 24, src: grid24, height: 'short' },
  { id: 25, src: grid25, height: 'tall' },
  { id: 26, src: grid26, height: 'medium' },
];

// Quote text split into words for animation
const quoteText = "אני מאמינה שכל בית צריך לספר את הסיפור של מי שגר בו. עיצוב טוב הוא לא רק יפה – הוא פונקציונלי, חכם ומותאם בדיוק לצרכים שלכם.";

const IntroSection = () => {
  const sectionRef = useRef(null);
  const galleryRef = useRef(null);
  const quoteContainerRef = useRef(null);
  const authorRef = useRef(null);
  const wordsRef = useRef([]);
  const masonryItemsRef = useRef([]);

  useEffect(() => {
    // Initialize ScrollReveal for masonry items
    const sr = ScrollReveal({
      reset: false,
      viewFactor: 0.15,
    });

    // Apply ScrollReveal to each masonry item with staggered delay
    masonryItemsRef.current.forEach((item, index) => {
      if (item) {
        sr.reveal(item, {
          duration: 800,
          distance: '20px',
          origin: 'bottom',
          opacity: 0,
          scale: 0.97,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          delay: index * 50,
          interval: 0,
        });
      }
    });

    // GSAP Quote Animation - Sequential Word Fill
    const words = wordsRef.current.filter(Boolean);
    const authorElement = authorRef.current;
    const totalWords = words.length;

    if (totalWords > 0) {
      ScrollTrigger.create({
        trigger: quoteContainerRef.current,
        start: 'top 85%',
        end: 'center 30%',
        scrub: 0.2,
        onUpdate: (self) => {
          const progress = self.progress;
          
          words.forEach((wordWrapper, index) => {
            const fillLayer = wordWrapper.querySelector(`.${styles.wordFill}`);
            if (!fillLayer) return;
            
            const wordStart = index / totalWords;
            const wordEnd = (index + 1) / totalWords;
            
            let wordProgress = 0;
            if (progress >= wordEnd) {
              wordProgress = 1;
            } else if (progress > wordStart) {
              wordProgress = (progress - wordStart) / (wordEnd - wordStart);
            }
            
            gsap.set(fillLayer, {
              clipPath: `inset(0 0 0 ${(1 - wordProgress) * 100}%)`
            });
          });
          
          const authorStart = 0.85;
          const authorProgress = Math.max(0, (progress - authorStart) / (1 - authorStart));
          gsap.set(authorElement, { opacity: authorProgress });
        }
      });
    }

    return () => {
      sr.destroy();
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const quoteWords = quoteText.split(' ');

  return (
    <section ref={sectionRef} className={styles.introSection}>
      
      {/* Infinite Marquee */}
      <div className={styles.marqueeWrapper}>
        <div className={styles.marqueeFade}></div>
        <div className={styles.marqueeContent}>
          <div className={styles.marqueeTrack}>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
          </div>
          <div className={styles.marqueeTrack} aria-hidden="true">
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
            <span className={styles.marqueeItem}>SG STUDIO | arch & design</span>
          </div>
        </div>
      </div>

      <div className={styles.container}>
        
        {/* Masonry Gallery */}
        <div ref={galleryRef} className={styles.gallerySection}>
          <div className={styles.masonryGrid}>
            {galleryImages.map((image, index) => (
              <div 
                key={image.id} 
                ref={el => masonryItemsRef.current[index] = el}
                className={`${styles.masonryItem} ${styles[image.height]}`}
              >
                <img 
                  src={image.src} 
                  alt="" 
                  className={styles.masonryImage}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Quote Section */}
        <div ref={quoteContainerRef} className={styles.quoteSection}>
          <blockquote className={styles.quote}>
            {quoteWords.map((word, index) => (
              <span 
                key={index}
                ref={el => wordsRef.current[index] = el}
                className={styles.wordWrapper}
              >
                <span className={styles.wordBase}>{word}</span>
                <span className={styles.wordFill}>{word}</span>
              </span>
            ))}
          </blockquote>
          
          <span ref={authorRef} className={styles.authorName}>שירלי גולדשטיין</span>
        </div>

      </div>
    </section>
  );
};

export default IntroSection;